import React, { useEffect, useState, useRef, useCallback } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { $getRoot, $getSelection, $isRangeSelection, $createParagraphNode, $isParagraphNode, $isTextNode } from "lexical";
import { HeadingNode, QuoteNode, $createHeadingNode, $createQuoteNode, $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode, INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND, registerListStrictIndentTransform } from "@lexical/list";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkNode } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND } from "lexical";
import { $isListNode } from "@lexical/list";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import DOMPurify from "dompurify";


const MEDIA_LIMITS = {
    image: {
      maxWidth: 1200,
      maxHeight: 800
    },
    video: {
      maxDuration: 60 
    }
  };

  function Toolbar() {
    const [editor] = useLexicalComposerContext();
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [textAlign, setTextAlign] = useState("left");
    const [isJustify, setIsJustify] = useState(false);
    const [isBulletList, setIsBulletList] = useState(false);
    const [isNumberedList, setIsNumberedList] = useState(false);
    const [currentStyle, setCurrentStyle] = useState("normal");
    const [currentFont, setCurrentFont] = useState("Arial");
    const [fontSize, setFontSize] = useState(16);
    const [fontSizeInput, setFontSizeInput] = useState("16");
    const [showStyleDropdown, setShowStyleDropdown] = useState(false);
    const [showFontDropdown, setShowFontDropdown] = useState(false);
    const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
    
    const updateToolbar = () => {
      try {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setIsBold(selection.hasFormat("bold"));
          setIsItalic(selection.hasFormat("italic"));
          setIsUnderline(selection.hasFormat("underline"));
          setIsStrikethrough(selection.hasFormat("strikethrough"));
          
          // Check if we're in a list
          const anchorNode = selection.anchor.getNode();
          let currentNode = anchorNode;
          let foundListNode = null;
          
          // Traverse up to find list node
          while (currentNode) {
            if ($isListNode(currentNode)) {
              foundListNode = currentNode;
              break;
            }
            const parent = currentNode.getParent();
            if (!parent || parent.getKey() === "root") break;
            currentNode = parent;
          }
          
          if (foundListNode) {
            const listType = foundListNode.getListType();
            setIsBulletList(listType === "bullet");
            setIsNumberedList(listType === "number");
          } else {
            setIsBulletList(false);
            setIsNumberedList(false);
          }
          
          // Get text align from the parent element
          const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
          const elementDOM = editor.getElementByKey(element.getKey());
          if (elementDOM) {
            const computedStyle = window.getComputedStyle(elementDOM);
            const align = computedStyle.textAlign || "left";
            setTextAlign(align);
            setIsJustify(align === "justify");
            
            // Detect current style
            let foundStyle = false;
            if ($isHeadingNode(element)) {
              const tag = element.getTag();
              if (tag === "h1") {
                setCurrentStyle("h1");
                foundStyle = true;
              } else if (tag === "h2") {
                setCurrentStyle("h2");
                foundStyle = true;
              } else if (tag === "h3") {
                setCurrentStyle("h3");
                foundStyle = true;
              }
            } else if ($isQuoteNode(element)) {
              setCurrentStyle("quote");
              foundStyle = true;
            } else if ($isListNode(element)) {
              const listType = element.getListType();
              if (listType === "bullet") {
                setCurrentStyle("bullet");
                foundStyle = true;
              } else if (listType === "number") {
                setCurrentStyle("number");
                foundStyle = true;
              }
            } else if ($isParagraphNode(element)) {
              setCurrentStyle("normal");
              foundStyle = true;
            }
            
            if (!foundStyle) {
              setCurrentStyle("normal");
            }
            
            // Detect current font and font size from Lexical nodes
            let fontFamily = "Arial";
            let fontSizeStr = "16px";
            
            // Get font-family and font-size from text nodes
            if ($isTextNode(anchorNode)) {
              const textStyle = anchorNode.getStyle() || "";
              if (textStyle) {
                const styles = textStyle.split(';').reduce((acc, s) => {
                  const pair = s.split(':');
                  if (pair.length >= 2) {
                    const key = pair[0].trim().toLowerCase();
                    const value = pair.slice(1).join(':').trim();
                    if (key && value) acc[key] = value;
                  }
                  return acc;
                }, {});
                
                if (styles['font-family']) {
                  fontFamily = styles['font-family'];
                }
                if (styles['font-size']) {
                  fontSizeStr = styles['font-size'];
                }
              }
            }
            
            // Also check parent elements for font-family and font-size
            let parent = anchorNode.getParent();
            while (parent && parent !== $getRoot() && (!fontFamily || fontFamily === "Arial" || !fontSizeStr || fontSizeStr === "16px")) {
              const parentStyle = parent.getStyle();
              if (parentStyle) {
                const styles = parentStyle.split(';').reduce((acc, s) => {
                  const pair = s.split(':');
                  if (pair.length >= 2) {
                    const key = pair[0].trim().toLowerCase();
                    const value = pair.slice(1).join(':').trim();
                    if (key && value) acc[key] = value;
                  }
                  return acc;
                }, {});
                
                if ((!fontFamily || fontFamily === "Arial") && styles['font-family']) {
                  fontFamily = styles['font-family'];
                }
                if ((!fontSizeStr || fontSizeStr === "16px") && styles['font-size']) {
                  fontSizeStr = styles['font-size'];
                }
              }
              parent = parent.getParent();
            }
            
            // Check the element's style attribute
            const elementStyle = element.getStyle();
            if (elementStyle) {
              const styles = elementStyle.split(';').reduce((acc, s) => {
                const pair = s.split(':');
                if (pair.length >= 2) {
                  const key = pair[0].trim().toLowerCase();
                  const value = pair.slice(1).join(':').trim();
                  if (key && value) acc[key] = value;
                }
                return acc;
              }, {});
              
              if ((!fontFamily || fontFamily === "Arial") && styles['font-family']) {
                fontFamily = styles['font-family'];
              }
              if ((!fontSizeStr || fontSizeStr === "16px") && styles['font-size']) {
                fontSizeStr = styles['font-size'];
              }
            }
            
            // Clean up font family string (remove quotes, fallbacks, etc.)
            const cleanFontFamily = fontFamily.replace(/['"]/g, '').split(',')[0].trim();
            
            const detectedFont = cleanFontFamily.includes("Courier New") ? "Courier New" :
                                cleanFontFamily.includes("Georgia") ? "Georgia" :
                                cleanFontFamily.includes("Times New Roman") ? "Times New Roman" :
                                cleanFontFamily.includes("Verdana") ? "Verdana" :
                                cleanFontFamily.includes("Trebuchet MS") ? "Trebuchet MS" : "Arial";
            setCurrentFont(detectedFont);
            
            const size = parseInt(fontSizeStr) || 16;
            // Round to nearest preset size or default to 16
            const presetSizes = [16, 18, 20, 24, 34];
            const nearestSize = presetSizes.reduce((prev, curr) => 
              Math.abs(curr - size) < Math.abs(prev - size) ? curr : prev
            );
            setFontSize(nearestSize);
            setFontSizeInput(size.toString()); // Update input with actual size
          }
        }
      } catch (error) {
        // Ignore errors when selection is not available
      }
    };
    
    useEffect(() => {
      return editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      });
    }, [editor]);
    
    const applyFormat = (format) => {
      editor.focus();
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    };
    
    const applyAlign = (align) => {
      editor.focus();
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
    };
    
    const applyList = (listType) => {
      editor.focus();
      editor.update(() => {
        if (listType === "bullet") {
          if (isBulletList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          }
        } else if (listType === "number") {
          if (isNumberedList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          }
        }
      });
    };
    
    const applyStyle = (style) => {
      editor.focus();
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          if (style === "bullet") {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            setShowStyleDropdown(false);
            return;
          } else if (style === "number") {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            setShowStyleDropdown(false);
            return;
          }
          
          // Get the anchor node and its top-level parent (paragraph/heading)
          // This should only be the current paragraph, not the root
          const anchorNode = selection.anchor.getNode();
          let topLevelNode;
          
          // If anchor is a text node, get its parent
          if ($isTextNode(anchorNode)) {
            topLevelNode = anchorNode.getParent();
            // Keep going up until we find a paragraph/heading/quote
            while (topLevelNode && !$isParagraphNode(topLevelNode) && !$isHeadingNode(topLevelNode) && !$isQuoteNode(topLevelNode)) {
              topLevelNode = topLevelNode.getParent();
            }
          } else {
            topLevelNode = anchorNode.getTopLevelElementOrThrow();
          }
          
          // Only replace the current paragraph/heading, not all paragraphs
          if (topLevelNode && topLevelNode !== $getRoot()) {
            const nodeChildren = topLevelNode.getChildren();
            
            let newNode;
            if (style === "normal") {
              newNode = $createParagraphNode();
            } else if (style === "h1") {
              newNode = $createHeadingNode("h1");
            } else if (style === "h2") {
              newNode = $createHeadingNode("h2");
            } else if (style === "h3") {
              newNode = $createHeadingNode("h3");
            } else if (style === "quote") {
              newNode = $createQuoteNode();
            }
            
            if (newNode) {
              // Only append children from this specific node
              newNode.append(...nodeChildren);
              topLevelNode.replace(newNode);
              // Restore selection to the end of the new node
              newNode.selectEnd();
            }
          }
        } else {
          // If no selection, apply to the paragraph where cursor is
          const root = $getRoot();
          const selection = $getSelection();
          if (selection) {
            const anchorNode = selection.anchor.getNode();
            const topLevelNode = anchorNode.getTopLevelElementOrThrow();
            
            let newNode;
            if (style === "normal") {
              newNode = $createParagraphNode();
            } else if (style === "h1") {
              newNode = $createHeadingNode("h1");
            } else if (style === "h2") {
              newNode = $createHeadingNode("h2");
            } else if (style === "h3") {
              newNode = $createHeadingNode("h3");
            } else if (style === "quote") {
              newNode = $createQuoteNode();
            }
            
            if (newNode) {
              const nodeChildren = topLevelNode.getChildren();
              newNode.append(...nodeChildren);
              topLevelNode.replace(newNode);
              newNode.selectEnd();
            }
          }
        }
      });
      setShowStyleDropdown(false);
    };
    
    const applyFont = (font) => {
      editor.focus();
    
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const nodes = selection.getNodes();
    
          nodes.forEach((node) => {
            if ($isTextNode(node)) {
              const existingStyle = node.getStyle() || "";
              node.setStyle(
                `${existingStyle}; font-family: ${font};`
              );
            }
          });
        }
      });
    
      setCurrentFont(font);
      setShowFontDropdown(false);
    };
    
    const applyFontSize = (size) => {
      const validSize = Math.max(8, Math.min(72, parseInt(size) || 16));
    
      editor.focus();
    
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const nodes = selection.getNodes();
    
          nodes.forEach((node) => {
            if ($isTextNode(node)) {
              const existingStyle = node.getStyle() || "";
              node.setStyle(
                `${existingStyle}; font-size: ${validSize}px;`
              );
            }
          });
        }
      });
    
      setFontSize(validSize);
      setFontSizeInput(validSize.toString());
      setShowFontSizeDropdown(false);
    };
    
    const handleFontSizeInputChange = (e) => {
      const value = e.target.value;
      setFontSizeInput(value);
    };
    
    const handleFontSizeInputBlur = () => {
      const validSize = Math.max(8, Math.min(72, parseInt(fontSizeInput) || 16));
      applyFontSize(validSize);
    };
    
    const handleFontSizeInputKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const validSize = Math.max(8, Math.min(72, parseInt(fontSizeInput) || 16));
        applyFontSize(validSize);
        e.target.blur();
      } else if (e.key === 'Escape') {
        e.target.blur();
      }
    };
    
    const changeFontSize = (delta) => {
      editor.focus();
    
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const nodes = selection.getNodes();
    
          nodes.forEach((node) => {
            if ($isTextNode(node)) {
              const existingStyle = node.getStyle() || "";
    
              const currentSizeMatch = existingStyle.match(/font-size:\s*(\d+)px/);
              const currentSize = currentSizeMatch ? parseInt(currentSizeMatch[1]) : 16;
    
              const newSize = Math.max(8, Math.min(72, currentSize + delta));
    
              node.setStyle(
                existingStyle.replace(/font-size:\s*\d+px;?/g, "") +
                ` font-size: ${newSize}px;`
              );
    
              setFontSize(newSize);
              setFontSizeInput(newSize.toString());
            }
          });
        }
      });
    };
    
  
    const buttonBaseStyle = {
      padding: "6px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      background: "#fff",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "600",
      transition: "all 0.2s ease"
    };
    
    const activeButtonStyle = {
      ...buttonBaseStyle,
      background: "#3b82f6",
      color: "#fff",
      borderColor: "#3b82f6"
    };
  
    const dropdownStyle = {
      position: "relative",
      display: "inline-block"
    };
    
    const dropdownButtonStyle = {
      ...buttonBaseStyle,
      display: "flex",
      alignItems: "center",
      gap: "6px",
      minWidth: "120px",
      justifyContent: "space-between"
    };
    
    const dropdownMenuStyle = {
      position: "absolute",
      top: "100%",
      left: 0,
      background: "#fff",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
      zIndex: 1000,
      minWidth: "200px",
      marginTop: "4px",
      maxHeight: "300px",
      overflowY: "auto"
    };
    
    const dropdownItemStyle = {
      padding: "8px 12px",
      cursor: "pointer",
      fontSize: "14px",
      borderBottom: "1px solid #f3f4f6",
      display: "flex",
      alignItems: "center",
      gap: "8px"
    };
    
    const dropdownItemHoverStyle = {
      ...dropdownItemStyle,
      background: "#f3f4f6"
    };
    
    // Icon components for dropdown
    const BulletListIcon = ({ isActive = false }) => (
      <span style={{ 
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        alignItems: "flex-start",
        justifyContent: "center",
        width: "20px",
        height: "14px",
        minWidth: "20px"
      }}>
        {[1, 2, 3].map((i) => (
          <span key={i} style={{ 
            display: "flex",
            alignItems: "center",
            gap: "4px",
            width: "100%"
          }}>
            <span style={{ 
              width: "3px", 
              height: "3px", 
              borderRadius: "50%", 
              background: isActive ? "#3b82f6" : "#333", 
              display: "block",
              flexShrink: 0
            }}></span>
            <span style={{ 
              width: "8px", 
              height: "1.5px", 
              background: isActive ? "#3b82f6" : "#333", 
              display: "block",
              flex: 1
            }}></span>
          </span>
        ))}
      </span>
    );
    
    const NumberedListIcon = ({ isActive = false }) => (
      <span style={{ 
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        alignItems: "flex-start",
        justifyContent: "center",
        width: "20px",
        height: "14px",
        fontSize: "9px",
        lineHeight: "1",
        fontWeight: "600",
        color: isActive ? "#3b82f6" : "#333",
        minWidth: "20px"
      }}>
        {[1, 2, 3].map((num) => (
          <span key={num} style={{ 
            display: "flex",
            alignItems: "center",
            gap: "4px",
            width: "100%"
          }}>
            <span style={{ flexShrink: 0 }}>{num}</span>
            <span style={{ 
              width: "8px", 
              height: "1.5px", 
              background: isActive ? "#3b82f6" : "#333", 
              display: "block",
              flex: 1
            }}></span>
          </span>
        ))}
      </span>
    );
    
    const styleOptions = [
      { value: "normal", label: "Normal", icon: "≡" },
      { value: "h1", label: "H1 Heading 1", icon: "H1" },
      { value: "h2", label: "H2 Heading 2", icon: "H2" },
      { value: "h3", label: "H3 Heading 3", icon: "H3" },
      { value: "number", label: "Numbered List", icon: <NumberedListIcon isActive={currentStyle === "number"} /> },
      { value: "bullet", label: "Bullet List", icon: <BulletListIcon isActive={currentStyle === "bullet"} /> },
      { value: "quote", label: "Quote", icon: '"' }
    ];
    
    const fontOptions = [
      "Arial",
      "Courier New",
      "Georgia",
      "Times New Roman",
      "Verdana",
      "Trebuchet MS"
    ];
    
    const fontSizeOptions = [16, 18, 20, 24, 34];
    
    const getStyleLabel = (value) => {
      const option = styleOptions.find(opt => opt.value === value);
      return option ? option.label : "Normal";
    };
    
    return (
      <div
        style={{
          display: "flex",
          gap: "8px",
          padding: "8px",
          borderBottom: "1px solid #e5e7eb",
          background: "#f9fafb",
          borderRadius: "8px 8px 0 0",
          flexWrap: "wrap"
        }}
      >
        {/* Style Dropdown */}
        <div style={dropdownStyle}>
          <button
            onClick={() => setShowStyleDropdown(!showStyleDropdown)}
            style={dropdownButtonStyle}
            onBlur={() => setTimeout(() => setShowStyleDropdown(false), 200)}
          >
            <span>{getStyleLabel(currentStyle)}</span>
            <span style={{ fontSize: "10px" }}>▼</span>
          </button>
          {showStyleDropdown && (
            <div style={dropdownMenuStyle}>
              {styleOptions.map((option) => (
                <div
                  key={option.value}
                  style={currentStyle === option.value ? dropdownItemHoverStyle : dropdownItemStyle}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyStyle(option.value);
                  }}
                  onMouseEnter={(e) => {
                    if (currentStyle !== option.value) {
                      e.currentTarget.style.background = "#f3f4f6";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentStyle !== option.value) {
                      e.currentTarget.style.background = "#fff";
                    }
                  }}
                >
                  <span style={{ fontWeight: "600", minWidth: "30px", display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                    {typeof option.icon === 'string' ? option.icon : option.icon}
                  </span>
                  <span>{option.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Font Dropdown */}
        <div style={dropdownStyle}>
          <button
            onClick={() => setShowFontDropdown(!showFontDropdown)}
            style={dropdownButtonStyle}
            onBlur={() => setTimeout(() => setShowFontDropdown(false), 200)}
          >
            <span>{currentFont}</span>
            <span style={{ fontSize: "10px" }}>▼</span>
          </button>
          {showFontDropdown && (
            <div style={dropdownMenuStyle}>
              {fontOptions.map((font) => (
                <div
                  key={font}
                  style={currentFont === font ? dropdownItemHoverStyle : dropdownItemStyle}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyFont(font);
                  }}
                  onMouseEnter={(e) => {
                    if (currentFont !== font) {
                      e.currentTarget.style.background = "#f3f4f6";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentFont !== font) {
                      e.currentTarget.style.background = "#fff";
                    }
                  }}
                >
                  <span style={{ fontFamily: font }}>{font}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Font Size Control with Dropdown */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          border: "1px solid #d1d5db",
          borderRadius: "4px",
          background: "#fff",
          padding: "0 4px",
          position: "relative"
        }}>
          <button
            onClick={() => changeFontSize(-1)}
            style={{
              ...buttonBaseStyle,
              padding: "4px 8px",
              border: "none",
              background: "transparent",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            −
          </button>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <input
              type="text"
              value={fontSizeInput}
              onChange={handleFontSizeInputChange}
              onBlur={handleFontSizeInputBlur}
              onKeyDown={handleFontSizeInputKeyDown}
              onClick={() => setShowFontSizeDropdown(!showFontSizeDropdown)}
              style={{
                width: "40px",
                padding: "4px 20px 4px 4px",
                border: "none",
                background: "transparent",
                fontSize: "14px",
                fontWeight: "600",
                textAlign: "center",
                outline: "none"
              }}
            />
            <button
              onClick={() => setShowFontSizeDropdown(!showFontSizeDropdown)}
              style={{
                ...buttonBaseStyle,
                padding: "0",
                border: "none",
                background: "transparent",
                position: "absolute",
                right: "4px",
                cursor: "pointer"
              }}
              onBlur={() => setTimeout(() => setShowFontSizeDropdown(false), 200)}
            >
              <span style={{ fontSize: "10px" }}>▼</span>
            </button>
            {showFontSizeDropdown && (
              <div style={{
                ...dropdownMenuStyle,
                position: "absolute",
                top: "100%",
                left: "0",
                marginTop: "4px",
                zIndex: 1000
              }}>
                {fontSizeOptions.map((size) => (
                  <div
                    key={size}
                    style={fontSize === size ? dropdownItemHoverStyle : dropdownItemStyle}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyFontSize(size);
                    }}
                    onMouseEnter={(e) => {
                      if (fontSize !== size) {
                        e.currentTarget.style.background = "#f3f4f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (fontSize !== size) {
                        e.currentTarget.style.background = "#fff";
                      }
                    }}
                  >
                    <span>{size}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => changeFontSize(1)}
            style={{
              ...buttonBaseStyle,
              padding: "4px 8px",
              border: "none",
              background: "transparent",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            +
          </button>
        </div>
        
        <div style={{ width: "1px", background: "#e5e7eb", margin: "0 4px" }}></div>
        
        <button 
          onClick={() => applyFormat("bold")}
          style={isBold ? activeButtonStyle : buttonBaseStyle}
        >
          B
        </button>
        <button 
          onClick={() => applyFormat("italic")}
          style={isItalic ? activeButtonStyle : buttonBaseStyle}
        >
          I
        </button>
        <button 
          onClick={() => applyFormat("underline")}
          style={isUnderline ? activeButtonStyle : buttonBaseStyle}
        >
          <span style={{ 
            textDecoration: "underline",
            color: isUnderline ? "#fff" : "inherit"
          }}>U</span>
        </button>
        <button 
          onClick={() => applyFormat("strikethrough")}
          style={isStrikethrough ? activeButtonStyle : buttonBaseStyle}
        >
          <span style={{ 
            textDecoration: "line-through",
            color: isStrikethrough ? "#fff" : "inherit"
          }}>S</span>
        </button>
        <button 
          onClick={() => applyAlign("left")}
          style={textAlign === "left" ? activeButtonStyle : buttonBaseStyle}
          title="Align Left"
        >
          <span style={{ 
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            alignItems: "flex-start",
            justifyContent: "center",
            width: "16px",
            height: "12px"
          }}>
            <span style={{ width: "12px", height: "1.5px", background: textAlign === "left" ? "#fff" : "#333", display: "block" }}></span>
            <span style={{ width: "8px", height: "1.5px", background: textAlign === "left" ? "#fff" : "#333", display: "block" }}></span>
            <span style={{ width: "12px", height: "1.5px", background: textAlign === "left" ? "#fff" : "#333", display: "block" }}></span>
          </span>
        </button>
        <button 
          onClick={() => applyAlign("center")}
          style={textAlign === "center" ? activeButtonStyle : buttonBaseStyle}
          title="Align Center"
        >
          <span style={{ 
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            alignItems: "center",
            justifyContent: "center",
            width: "16px",
            height: "12px"
          }}>
            <span style={{ width: "12px", height: "1.5px", background: textAlign === "center" ? "#fff" : "#333", display: "block" }}></span>
            <span style={{ width: "8px", height: "1.5px", background: textAlign === "center" ? "#fff" : "#333", display: "block" }}></span>
            <span style={{ width: "12px", height: "1.5px", background: textAlign === "center" ? "#fff" : "#333", display: "block" }}></span>
          </span>
        </button>
        <button 
          onClick={() => applyAlign("right")}
          style={textAlign === "right" ? activeButtonStyle : buttonBaseStyle}
          title="Align Right"
        >
          <span style={{ 
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            alignItems: "flex-end",
            justifyContent: "center",
            width: "16px",
            height: "12px"
          }}>
            <span style={{ width: "12px", height: "1.5px", background: textAlign === "right" ? "#fff" : "#333", display: "block" }}></span>
            <span style={{ width: "8px", height: "1.5px", background: textAlign === "right" ? "#fff" : "#333", display: "block" }}></span>
            <span style={{ width: "12px", height: "1.5px", background: textAlign === "right" ? "#fff" : "#333", display: "block" }}></span>
          </span>
        </button>
        <button 
          onClick={() => applyAlign("justify")}
          style={textAlign === "justify" ? activeButtonStyle : buttonBaseStyle}
          title="Justify"
        >
          <span style={{ 
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            alignItems: "stretch",
            justifyContent: "center",
            width: "16px",
            height: "12px"
          }}>
            <span style={{ width: "100%", height: "1.5px", background: textAlign === "justify" ? "#fff" : "#333", display: "block" }}></span>
            <span style={{ width: "100%", height: "1.5px", background: textAlign === "justify" ? "#fff" : "#333", display: "block" }}></span>
            <span style={{ width: "100%", height: "1.5px", background: textAlign === "justify" ? "#fff" : "#333", display: "block" }}></span>
          </span>
        </button>
        <button 
          onClick={() => applyList("bullet")}
          style={isBulletList ? activeButtonStyle : buttonBaseStyle}
          title="Bullet List"
        >
          <span style={{ 
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            alignItems: "flex-start",
            justifyContent: "center",
            width: "20px",
            height: "14px"
          }}>
            <span style={{ 
              display: "flex",
              alignItems: "center",
              gap: "4px",
              width: "100%"
            }}>
              <span style={{ 
                width: "3px", 
                height: "3px", 
                borderRadius: "50%", 
                background: isBulletList ? "#fff" : "#333", 
                display: "block",
                flexShrink: 0
              }}></span>
              <span style={{ 
                width: "8px", 
                height: "1.5px", 
                background: isBulletList ? "#fff" : "#333", 
                display: "block",
                flex: 1
              }}></span>
            </span>
            <span style={{ 
              display: "flex",
              alignItems: "center",
              gap: "4px",
              width: "100%"
            }}>
              <span style={{ 
                width: "3px", 
                height: "3px", 
                borderRadius: "50%", 
                background: isBulletList ? "#fff" : "#333", 
                display: "block",
                flexShrink: 0
              }}></span>
              <span style={{ 
                width: "8px", 
                height: "1.5px", 
                background: isBulletList ? "#fff" : "#333", 
                display: "block",
                flex: 1
              }}></span>
            </span>
            <span style={{ 
              display: "flex",
              alignItems: "center",
              gap: "4px",
              width: "100%"
            }}>
              <span style={{ 
                width: "3px", 
                height: "3px", 
                borderRadius: "50%", 
                background: isBulletList ? "#fff" : "#333", 
                display: "block",
                flexShrink: 0
              }}></span>
              <span style={{ 
                width: "8px", 
                height: "1.5px", 
                background: isBulletList ? "#fff" : "#333", 
                display: "block",
                flex: 1
              }}></span>
            </span>
          </span>
        </button>
        <button 
          onClick={() => applyList("number")}
          style={isNumberedList ? activeButtonStyle : buttonBaseStyle}
          title="Numbered List"
        >
          <span style={{ 
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            alignItems: "flex-start",
            justifyContent: "center",
            width: "20px",
            height: "14px",
            fontSize: "9px",
            lineHeight: "1",
            fontWeight: "600",
            color: isNumberedList ? "#fff" : "#333"
          }}>
            <span style={{ 
              display: "flex",
              alignItems: "center",
              gap: "4px",
              width: "100%"
            }}>
              <span style={{ flexShrink: 0 }}>1</span>
              <span style={{ 
                width: "8px", 
                height: "1.5px", 
                background: isNumberedList ? "#fff" : "#333", 
                display: "block",
                flex: 1
              }}></span>
            </span>
            <span style={{ 
              display: "flex",
              alignItems: "center",
              gap: "4px",
              width: "100%"
            }}>
              <span style={{ flexShrink: 0 }}>2</span>
              <span style={{ 
                width: "8px", 
                height: "1.5px", 
                background: isNumberedList ? "#fff" : "#333", 
                display: "block",
                flex: 1
              }}></span>
            </span>
            <span style={{ 
              display: "flex",
              alignItems: "center",
              gap: "4px",
              width: "100%"
            }}>
              <span style={{ flexShrink: 0 }}>3</span>
              <span style={{ 
                width: "8px", 
                height: "1.5px", 
                background: isNumberedList ? "#fff" : "#333", 
                display: "block",
                flex: 1
              }}></span>
            </span>
          </span>
        </button>
      </div>
    );
  }
  

  /**
   * Converts Lexical editor state to sanitized HTML on every change.
   * This ensures we always save HTML strings, never Lexical JSON.
   * 
   * Flow: Lexical State → HTML → DOMPurify.sanitize() → HTML string → save to DynamoDB/S3
   */
  function OnChangePluginWrapper({ onChange }) {
    const [editor] = useLexicalComposerContext();
    
    return (
        <OnChangePlugin
          onChange={(editorState) => {
            editorState.read(() => {
              // Convert Lexical state → HTML
              const html = $generateHtmlFromNodes(editor, null);
              // Sanitize HTML before saving (removes XSS risks)
              const safeHtml = DOMPurify.sanitize(html);
              // Pass sanitized HTML to parent component
              onChange(safeHtml);
            });
          }}
        />
    );
  }

  function ContentEditableWithTabSupport({ style }) {
    return (
      <ContentEditable
        style={style}
      />
    );
  }
  
  // Proper Lexical way to access the editor instance after mount
  function EditorReadyPlugin({ onReady }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
      if (!editor) return;
      // Register strict indent transform for nested lists (Tab/Shift+Tab support)
      registerListStrictIndentTransform(editor);
      onReady?.(editor);
    }, [editor]);

    return null;
  }
  
  /**
   * Recursively extracts text nodes with their inherited styles from DOM
   * Returns an array of { text, style } objects in document order
   */
  function extractTextNodesWithStyles(domNode, parentStyle = '', result = []) {
    if (!domNode) return result;
    
    // Get inline style from this element
    const inlineStyle = domNode.getAttribute ? (domNode.getAttribute('style') || '') : '';
    // Combine parent and current styles
    const currentStyle = parentStyle && inlineStyle 
      ? `${parentStyle}; ${inlineStyle}`.replace(/\s*;\s*/g, '; ').replace(/^;\s*|;\s*$/g, '')
      : (parentStyle || inlineStyle);
    
    // Process child nodes
    for (let i = 0; i < domNode.childNodes.length; i++) {
      const child = domNode.childNodes[i];
      
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent;
        if (text) {
          result.push({ text: text, style: currentStyle });
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        extractTextNodesWithStyles(child, currentStyle, result);
      }
    }
    
    return result;
  }
  
  /**
   * Applies styles to Lexical text nodes by matching them with extracted DOM text nodes
   * Uses a character-by-character matching approach for accuracy
   */
  function applyStylesToLexicalNodes(lexicalNode, textNodesWithStyles, position = { domIndex: 0, domOffset: 0 }) {
    if ($isTextNode(lexicalNode)) {
      const lexicalText = lexicalNode.getTextContent();
      if (lexicalText && position.domIndex < textNodesWithStyles.length) {
        let remainingLexicalText = lexicalText;
        const styles = [];
        let currentDomIndex = position.domIndex;
        let currentDomOffset = position.domOffset;
        
        // Match Lexical text with DOM text nodes character by character
        while (remainingLexicalText.length > 0 && currentDomIndex < textNodesWithStyles.length) {
          const domNode = textNodesWithStyles[currentDomIndex];
          const domText = domNode.text;
          const availableDomText = domText.substring(currentDomOffset);
          
          if (availableDomText.length === 0) {
            // This DOM node is exhausted, move to next
            currentDomIndex++;
            currentDomOffset = 0;
            continue;
          }
          
          // Find how many characters match
          const matchLength = Math.min(remainingLexicalText.length, availableDomText.length);
          const lexicalChunk = remainingLexicalText.substring(0, matchLength);
          const domChunk = availableDomText.substring(0, matchLength);
          
          // Only match if characters are the same (handle whitespace differences)
          const lexicalNormalized = lexicalChunk.replace(/\s+/g, ' ');
          const domNormalized = domChunk.replace(/\s+/g, ' ');
          
          if (lexicalNormalized === domNormalized || 
              (lexicalChunk.trim() && domChunk.trim() && lexicalChunk.trim() === domChunk.trim())) {
            // Characters match - collect the style
            if (domNode.style) {
              styles.push(domNode.style);
            }
            
            remainingLexicalText = remainingLexicalText.substring(matchLength);
            currentDomOffset += matchLength;
            
            // If we've consumed this DOM node, move to next
            if (currentDomOffset >= domText.length) {
              currentDomIndex++;
              currentDomOffset = 0;
            }
          } else {
            // No match - try next DOM node
            currentDomIndex++;
            currentDomOffset = 0;
          }
        }
        
        // Apply the collected styles (merge unique properties)
        if (styles.length > 0) {
          const styleMap = new Map();
          
          // Parse all styles and collect unique properties
          styles.forEach(styleStr => {
            if (styleStr) {
              const props = styleStr.split(';').map(s => s.trim()).filter(s => s);
              props.forEach(prop => {
                const [key, ...valueParts] = prop.split(':').map(s => s.trim());
                if (key && valueParts.length > 0) {
                  styleMap.set(key.toLowerCase(), `${key}: ${valueParts.join(':')}`);
                }
              });
            }
          });
          
          // Combine into final style string
          const mergedStyle = Array.from(styleMap.values()).join('; ');
          if (mergedStyle) {
            lexicalNode.setStyle(mergedStyle);
          }
        }
        
        // Update position for next node
        position.domIndex = currentDomIndex;
        position.domOffset = currentDomOffset;
      }
    }
    
    // Recursively process children
    if (lexicalNode.getChildren) {
      const children = lexicalNode.getChildren();
      children.forEach(child => applyStylesToLexicalNodes(child, textNodesWithStyles, position));
    }
  }

  function LexicalEditor({ value, onChange, minHeight = "160px", noBorder = false, showToolbar = true, onEditorReady }) {
    const isSmall = parseInt(minHeight) <= 40;
    
    // ASSUMPTION: value is always an HTML string (or null/empty)
    // Lexical is used only as an editor - we convert HTML → Lexical on load, Lexical → HTML on save
    const htmlValue = typeof value === 'string' ? value : '';
    const isEmpty = !htmlValue || htmlValue.trim() === '';
    
    // Create initial editor state from HTML
let editorState;

if (!isEmpty) {
  editorState = (editor) => {
    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(htmlValue, "text/html");
      
      // Extract text nodes with their styles from the original DOM before conversion
      const body = dom.body || dom.documentElement;
      const textNodesWithStyles = extractTextNodesWithStyles(body, '', []);

      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();

      if (nodes.length > 0) {
        nodes.forEach((node) => {
          // Apply styles from the original DOM to Lexical nodes
          applyStylesToLexicalNodes(node, textNodesWithStyles, { domIndex: 0, domOffset: 0 });
          root.append(node);
        });
      } else {
        // REQUIRED fallback — Lexical must never have empty root
        root.append($createParagraphNode());
      }
    });
  };
} else {
  // Empty value case
  editorState = (editor) => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      root.append($createParagraphNode());
    });
  };
}

    
    const initialConfig = {
      namespace: "LexicalEditor",
      editorState: editorState,
    onError(error) {
      console.error(error);
      },
      theme: {
        text: {
          bold: 'editor-text-bold',
          italic: 'editor-text-italic',
          underline: 'editor-text-underline',
          strikethrough: 'editor-text-strikethrough',
        }
      },
      nodes: [
        HeadingNode,
        QuoteNode,
        ListNode,
        ListItemNode,
        LinkNode
      ]
    };
    
    return (
      <div style={{
        border: noBorder ? "none" : "1px solid #ddd",
        borderRadius: noBorder ? "0" : "8px",
        overflow: noBorder ? "visible" : "hidden",
        background: noBorder ? "transparent" : "#fff"
      }}>
        <style>{`
          [data-lexical-editor="true"] {
            outline: none;
            min-height: ${isSmall ? "22px" : minHeight};
            max-height: ${isSmall ? "22px" : "none"};
            height: ${isSmall ? "22px" : "auto"};
            color: #333 !important;
            padding: ${isSmall ? "0" : "12px"};
            width: 100%;
            overflow: ${isSmall ? "hidden" : "auto"};
            display: block;
            white-space: ${isSmall ? "nowrap" : "pre-wrap"};
            background: transparent;
            position: relative;
            z-index: 2;
          }
          [data-lexical-editor="true"] p {
            margin: 0 0 8px 0 !important;
            padding: 0 !important;
            color: #333 !important;
            min-height: 1.5em;
          }
          [data-lexical-editor="true"] p:last-child {
            margin-bottom: 0 !important;
          }
          [data-lexical-editor="true"] h1 {
  font-size: 28px !important;
  line-height: 36px !important;
  font-weight: 700 !important;
  margin: 0 0 16px 0 !important;
  color: #101828 !important;
}

[data-lexical-editor="true"] h2 {
  font-size: 21px !important;
  line-height: 28px !important;
  font-weight: 700 !important;
  margin: 0 0 14px 0 !important;
  color: #101828 !important;
}

[data-lexical-editor="true"] h3 {
  font-size: 16px !important;
  line-height: 24px !important;
  font-weight: 700 !important;
  margin: 0 0 12px 0 !important;
  color: #101828 !important;
}

          [data-lexical-editor="true"] blockquote {
            border-left: 4px solid #ddd !important;
            padding-left: 16px !important;
            margin: 8px 0 !important;
            color: #666 !important;
            font-style: italic !important;
          }
          [data-lexical-editor="true"] p span {
            color: #333 !important;
          }
          [data-lexical-editor="true"] span[data-lexical-text="true"] {
            color: #333 !important;
          }
          [data-lexical-editor="true"] span {
            color: #333 !important;
          }
          [data-lexical-editor="true"] div {
            color: #333 !important;
          }
          [data-lexical-editor="true"] * {
            color: #333 !important;
            opacity: 1 !important;
          }
          [data-lexical-editor="true"] [contenteditable="true"] {
            color: #333 !important;
          }
          [data-lexical-editor="true"] [contenteditable="true"] * {
            color: #333 !important;
            opacity: 1 !important;
          }
          [data-lexical-editor="true"] .editor-text-bold {
            font-weight: bold !important;
          }
          [data-lexical-editor="true"] .editor-text-italic {
            font-style: italic !important;
          }
          [data-lexical-editor="true"] .editor-text-underline {
            text-decoration: underline !important;
          }
          [data-lexical-editor="true"] .editor-text-strikethrough {
            text-decoration: line-through !important;
          }
          [data-lexical-editor="true"] .editor-text-bold.editor-text-italic,
          [data-lexical-editor="true"] .editor-text-italic.editor-text-bold,
          [data-lexical-editor="true"] span.editor-text-bold.editor-text-italic,
          [data-lexical-editor="true"] span.editor-text-italic.editor-text-bold {
            font-weight: bold !important;
            font-style: italic !important;
          }
          [data-lexical-editor="true"] span.editor-text-bold {
            font-weight: bold !important;
          }
          [data-lexical-editor="true"] span.editor-text-italic {
            font-style: italic !important;
          }
          [data-lexical-editor="true"] span.editor-text-underline {
            text-decoration: underline !important;
          }
          [data-lexical-editor="true"] span.editor-text-strikethrough {
            text-decoration: line-through !important;
          }
          [data-lexical-editor="true"] span.editor-text-underline.editor-text-strikethrough,
          [data-lexical-editor="true"] span.editor-text-strikethrough.editor-text-underline {
            text-decoration: underline line-through !important;
          }
          [data-lexical-editor="true"] u {
            text-decoration: underline !important;
          }
          [data-lexical-editor="true"] s {
            text-decoration: line-through !important;
          }
          [data-lexical-editor="true"] [style*="text-decoration: underline"] {
            text-decoration: underline !important;
          }
          [data-lexical-editor="true"] [style*="text-decoration: line-through"] {
            text-decoration: line-through !important;
          }
          [data-lexical-editor="true"] em {
            font-style: italic !important;
          }
          [data-lexical-editor="true"] strong {
            font-weight: bold !important;
          }
          [data-lexical-editor="true"] strong em,
          [data-lexical-editor="true"] em strong {
            font-weight: bold !important;
            font-style: italic !important;
          }
          [data-lexical-editor="true"] ul,
          [data-lexical-editor="true"] ol {
            padding-left: 20px !important;
            margin: 0 !important;
          }
          /* Center alignment for lists - use more specific selectors */
          [data-lexical-editor="true"] ul[style*="center"],
          [data-lexical-editor="true"] ol[style*="center"] {
            list-style-position: inside !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            text-align: center !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
          }
          [data-lexical-editor="true"] ul[style*="center"] li,
          [data-lexical-editor="true"] ol[style*="center"] li {
            text-align: center !important;
            display: list-item !important;
            list-style-position: inside !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
          }
          /* Right alignment for lists */
          [data-lexical-editor="true"] ul[style*="right"],
          [data-lexical-editor="true"] ol[style*="right"] {
            list-style-position: inside !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            text-align: right !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
          }
          [data-lexical-editor="true"] ul[style*="right"] li,
          [data-lexical-editor="true"] ol[style*="right"] li {
            text-align: right !important;
            display: list-item !important;
            list-style-position: inside !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
          }
          /* Justify alignment for lists */
          [data-lexical-editor="true"] ul[style*="justify"],
          [data-lexical-editor="true"] ol[style*="justify"] {
            list-style-position: inside !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            text-align: justify !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
          }
          [data-lexical-editor="true"] ul[style*="justify"] li,
          [data-lexical-editor="true"] ol[style*="justify"] li {
            text-align: justify !important;
            display: list-item !important;
            list-style-position: inside !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
          }
          /* Also handle when alignment is on list items directly */
          [data-lexical-editor="true"] li[style*="center"] {
            list-style-position: inside !important;
            text-align: center !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          [data-lexical-editor="true"] li[style*="right"] {
            list-style-position: inside !important;
            text-align: right !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          [data-lexical-editor="true"] li[style*="justify"] {
            list-style-position: inside !important;
            text-align: justify !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          [data-lexical-editor="true"] li {
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Universal fix for all aligned list items - ensure markers align */
          [data-lexical-editor="true"] ul li,
          [data-lexical-editor="true"] ol li {
            list-style-position: inside !important;
          }
          /* Override for any list with alignment */
          [data-lexical-editor="true"] ul[style*="center"] li,
          [data-lexical-editor="true"] ol[style*="center"] li,
          [data-lexical-editor="true"] ul[style*="right"] li,
          [data-lexical-editor="true"] ol[style*="right"] li,
          [data-lexical-editor="true"] ul[style*="justify"] li,
          [data-lexical-editor="true"] ol[style*="justify"] li,
          [data-lexical-editor="true"] li[style*="center"],
          [data-lexical-editor="true"] li[style*="right"],
          [data-lexical-editor="true"] li[style*="justify"] {
            list-style-position: inside !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          /* Nested lists support */
          [data-lexical-editor="true"] ul ul,
          [data-lexical-editor="true"] ol ol,
          [data-lexical-editor="true"] ul ol,
          [data-lexical-editor="true"] ol ul {
            margin-left: 20px !important;
            margin-top: 4px !important;
            margin-bottom: 4px !important;
          }
          [data-lexical-editor="true"] br {
            display: block;
            content: "";
            margin-top: 4px;
          }
          [data-lexical-editor="true"] p:empty {
            min-height: 1.5em;
          }
          [data-lexical-editor="true"] p:empty::before {
            content: '';
          }
        `}</style>
        <LexicalComposer initialConfig={initialConfig}>
          {showToolbar && <Toolbar />}
          <EditorReadyPlugin onReady={onEditorReady} />
        <RichTextPlugin
            contentEditable={
              <ContentEditableWithTabSupport
                style={{
                  outline: "none",
                  minHeight: isSmall ? "22px" : minHeight,
                  //fontSize: "14px",
                  //lineHeight: isSmall ? "1.4" : "1.6"
                }}
              />
            }
            placeholder={null}
          />
          <ListPlugin />
        <HistoryPlugin />
        <OnChangePluginWrapper onChange={onChange} />
      </LexicalComposer>
      </div>
    );
  }


  function inferMediaTypeFromUrl(url) {
    const lower = url.toLowerCase();
    if (lower.match(/\.(png|jpg|jpeg|gif|webp)$/)) return "image";
    if (lower.match(/\.(mp4|webm|ogg)$/)) return "video";
    return null;
  }
  
  /**
   * Simple HTML renderer for viewing formatted content.
   * ASSUMPTION: content is always an HTML string (sanitized).
   * No JSON support - we only store HTML in DynamoDB/S3.
   * 
   * Styles are separate from editor styles to avoid conflicts.
   */
  function LexicalViewer({ content }) {
    if (!content || typeof content !== 'string') {
    return (
        <div style={{
          background: "#f8f9fa",
          padding: "15px",
          borderRadius: "6px",
          border: "1px solid #e9ecef",
          fontSize: "14px",
          lineHeight: "1.6",
          fontStyle: "italic",
          color: "#6c757d"
        }}>
          No content
        </div>
      );
    }
    
    // Content is HTML string - render directly
    // HTML is already sanitized when saved (via DOMPurify in OnChangePluginWrapper)
    return (
      <>
        <style>{`
          /* Viewer-specific styles - separate from editor styles */
          .lexical-viewer-content {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 6px;
            border: 1px solid #e9ecef;
            font-size: 14px;
            line-height: 1.6;
            color: #333;
          }
          .lexical-viewer-content p {
            margin: 0 0 8px 0;
          }
          .lexical-viewer-content p:last-child {
            margin-bottom: 0;
          }
          .lexical-viewer-content h1 {
  font-size: 28px;
  line-height: 36px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #101828;
}

.lexical-viewer-content h2 {
  font-size: 21px;
  line-height: 28px;
  font-weight: 700;
  margin-bottom: 14px;
  color: #101828;
}

.lexical-viewer-content h3 {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #101828;
}

          .lexical-viewer-content blockquote {
            border-left: 4px solid #ddd;
            padding-left: 16px;
            margin: 8px 0;
            color: #666;
            font-style: italic;
          }
          .lexical-viewer-content ul,
          .lexical-viewer-content ol {
            padding-left: 20px;
            margin: 8px 0;
          }
          .lexical-viewer-content li {
            margin: 4px 0;
          }
          .lexical-viewer-content ul ul,
          .lexical-viewer-content ol ol,
          .lexical-viewer-content ul ol,
          .lexical-viewer-content ol ul {
            margin-left: 20px;
            margin-top: 4px;
            margin-bottom: 4px;
          }
        `}</style>
        <div 
          className="lexical-viewer-content"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </>
    );
  }

  const API = "/api/whats-new";

async function api(url, method, body) {
  const apiBase = process.env.NODE_ENV === 'production' 
    ? 'https://admin.highperformr.ai' 
    : 'http://localhost:4000';
  
  const res = await fetch(`${apiBase}${url}`, {
    method,
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined
  });
  
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`API error: ${res.status} ${errorText}`);
  }
  
  return res.json();
}

async function validateMedia(media) {
    if (!media) return null;
  
    if (media.type === "image") {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const warnings = [];
  
          if (img.width > MEDIA_LIMITS.image.maxWidth) {
            warnings.push(`Image width is ${img.width}px (recommended ≤ ${MEDIA_LIMITS.image.maxWidth}px)`);
          }
  
          if (img.height > MEDIA_LIMITS.image.maxHeight) {
            warnings.push(`Image height is ${img.height}px (recommended ≤ ${MEDIA_LIMITS.image.maxHeight}px)`);
          }
  
          resolve(warnings.length ? warnings : null);
        };
        img.onerror = () => resolve(["Failed to load image metadata"]);
        img.src = media.url;
      });
    }
  
    if (media.type === "video") {
      return new Promise((resolve) => {
        const video = document.createElement("video");
        video.onloadedmetadata = () => {
          const warnings = [];
  
          if (video.duration > MEDIA_LIMITS.video.maxDuration) {
            warnings.push(`Video duration is ${Math.round(video.duration)}s (recommended ≤ ${MEDIA_LIMITS.video.maxDuration}s)`);
          }
  
          resolve(warnings.length ? warnings : null);
        };
        video.onerror = () => resolve(["Failed to load video metadata"]);
        video.src = media.url;
      });
    }
  
    return null;
  }
  

export default function WhatsNew() {
    /* ---------------- STATE ---------------- */
  
    const [changelogs, setChangelogs] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [changelogContent, setChangelogContent] = useState(null);
    const [changelogHeading, setChangelogHeading] = useState(""); // Separate heading field
    const [previewChangelogId, setPreviewChangelogId] = useState(null); // Store preview ID for new changelog
    const [banner, setBanner] = useState({ show: false, message: "", type: "success" }); // type: "success" | "error"
    const [confirmDialog, setConfirmDialog] = useState({ show: false, message: "", onConfirm: null, onCancel: null });
    const [activeTab, setActiveTab] = useState('configuration'); // 'configuration' | 'history'
    const [usePopupMedia, setUsePopupMedia] = useState(false);
    const [copyPopupContent, setCopyPopupContent] = useState(false);
    const [popupData, setPopupData] = useState(null); // Stores loaded popup data from API
    const changelogEditorRef = useRef(null); // Reference to Lexical editor instance
    const [pendingCopyPopupContent, setPendingCopyPopupContent] = useState(false);
    const popupFetchPromiseRef = useRef(null);
    const [existingChangelogMedia, setExistingChangelogMedia] = useState(null); // Media from changelog being edited
    const [keepExistingMedia, setKeepExistingMedia] = useState(true); // Whether to keep existing media when editing
    
    // Add global style to ensure text is visible
    React.useEffect(() => {
      const style = document.createElement('style');
      style.textContent = `
        .editor, .editor *, .editor p, .editor span, .editor div, 
        .editor [contenteditable="true"], .editor [contenteditable="true"] * {
          color: #333 !important;
          opacity: 1 !important;
        }
      `;
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
      };
    }, []);
  
    const [popup, setPopup] = useState({
      heading: "",
      description: "",
        mediaUrl: "",
        mediaType: "image", // "image" | "video"
        mediaSource: "url", // "url" | "upload"
        mediaFile: null, // File object when uploading
        uploadedMediaUrl: "", // URL after upload
      changelogId: "", // Changelog ID instead of knowMoreUrl
      tryNowUrl: "",
      openInSameTab: false
    });
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [publishTarget, setPublishTarget] = useState("production"); // "production" | "staging"
    const [stagingExists, setStagingExists] = useState(false);
    const [stagingBannerAction, setStagingBannerAction] = useState("promote"); // "promote" | "delete"
    const [stagingActionLoading, setStagingActionLoading] = useState(false);
  
    /* ---------------- LOAD ---------------- */
  
    useEffect(() => {
      loadChangelogs();
      loadPopup(); // Load popup data on mount
      loadStagingStatus();
    }, []);

    async function loadStagingStatus() {
      try {
        const res = await api(`${API}/staging-status`, "GET");
        setStagingExists(Boolean(res?.data?.exists));
        if (!res?.data?.exists) {
          setStagingBannerAction("promote");
        }
      } catch (error) {
        console.error('Error loading staging status:', error);
        setStagingExists(false);
      }
    }

    async function loadPopup() {
      try {
        const res = await api(`${API}/popup-details`, "GET");
        if (res && res.data) {
          const popupData = res.data;
          setPopup({
            heading: popupData.content?.heading || "",
            description: popupData.content?.description || "",
            mediaUrl: popupData.content?.media?.url || "",
            mediaType: popupData.content?.media?.type || "image",
            mediaSource: popupData.content?.media?.url ? "url" : "upload",
            mediaFile: null,
            uploadedMediaUrl: popupData.content?.media?.url || "",
            changelogId: extractChangelogIdFromKnowMore(popupData.content?.knowMore?.url),
            tryNowUrl: popupData.content?.tryNow?.url || "",
            openInSameTab: popupData.content?.tryNow?.openInSameTab ?? false
          });
        }
      } catch (error) {
        console.error('Error loading popup:', error);
      }
    }

    // Extract changelog ID from knowMore URL
    function extractChangelogIdFromKnowMore(url) {
      if (!url || typeof url !== 'string') return "";
      // URL format: https://www.highperformr.ai//changelogs#{changelog_id}
      const match = url.match(/changelogs#(.+)$/);
      return match ? match[1] : "";
    }
  
    async function loadChangelogs() {
      const res = await api(`${API}/changelog?limit=50`, "GET");
      setChangelogs(res.data || []);
    }

    // Generate preview ID for new changelog (same logic as backend)
    function generatePreviewChangelogId() {
      // Generate 6 random bytes as hex (12 characters)
      const randomBytes = new Uint8Array(6);
      crypto.getRandomValues(randomBytes);
      const hex = Array.from(randomBytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      return `clg_${hex}`;
    }

    const ensurePopupData = useCallback(async () => {
      if (popupData && popupData.content) {
        console.log('Using cached popup data:', popupData);
        return popupData;
      }

      if (popupFetchPromiseRef.current) {
        console.log('Popup fetch already in progress, waiting...');
        return popupFetchPromiseRef.current;
      }

      popupFetchPromiseRef.current = (async () => {
        try {
          console.log('Fetching popup data from API...');
          const res = await api(`${API}/popup-details`, "GET");
          console.log('Popup API response:', res);
          const data = res?.data || null;
          console.log('Extracted popup data:', data);
          
          if (data && data.content) {
            console.log('Popup data has content:', data.content);
            setPopupData(data);
            return data;
          } else {
            console.warn('Popup data missing content:', data);
            return null;
          }
        } catch (error) {
          console.error('Error fetching popup:', error);
          return null;
        } finally {
          popupFetchPromiseRef.current = null;
        }
      })();

      return popupFetchPromiseRef.current;
    }, [popupData]);

    /**
     * Injects popup heading and description into changelog fields
     * Heading goes to heading field, description goes to Lexical editor
     * Uses useCallback to ensure it always has access to latest state
     */
    const injectPopupContentIntoEditor = useCallback((popup) => {
      if (!popup || !popup.content) return false;

      const heading = popup.content.heading || '';
      const description = popup.content.description || '';

      if (!heading && !description) {
        return false;
      }

      // Set heading in the heading field
      if (heading) {
        setChangelogHeading(heading);
      }

      // Set description in Lexical editor
      if (description && changelogEditorRef.current) {
        try {
          // Generate HTML from description only
          const html = `<p>${DOMPurify.sanitize(description)}</p>`;

          // Inject into Lexical editor
          changelogEditorRef.current.update(() => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(html, 'text/html');
            const nodes = $generateNodesFromDOM(changelogEditorRef.current, dom);
            const root = $getRoot();
            root.clear();
            
            if (nodes.length > 0) {
              nodes.forEach(node => {
                // Apply styles from original DOM
                const body = dom.body || dom.documentElement;
                const textNodesWithStyles = extractTextNodesWithStyles(body, '', []);
                applyStylesToLexicalNodes(node, textNodesWithStyles, { domIndex: 0, domOffset: 0 });
                root.append(node);
              });
            } else {
              root.append($createParagraphNode());
            }
          });
        } catch (error) {
          console.error('Error injecting description into editor:', error);
        }
      }

      return true;
    }, []);

    const handleCopyPopupHeadingDescription = useCallback(async () => {
      console.log('handleCopyPopupHeadingDescription called');
      setPendingCopyPopupContent(true);
      const popup = await ensurePopupData();
      console.log('Popup data received:', popup);

      if (!popup) {
        console.error('Popup is null/undefined');
        setBanner({ show: true, message: "Popup not found. Please save the popup configuration first.", type: "error" });
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
        setPendingCopyPopupContent(false);
        return false;
      }

      if (!popup.content) {
        console.error('Popup content is missing:', popup);
        setBanner({ show: true, message: "Popup content is missing. Please save the popup configuration with heading and description.", type: "error" });
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
        setPendingCopyPopupContent(false);
        return false;
      }

      // If editor is ready, inject immediately; otherwise we'll inject when editor becomes ready
      if (changelogEditorRef.current) {
        const ok = injectPopupContentIntoEditor(popup);
        if (!ok) {
          setBanner({ show: true, message: "Popup heading/description is empty.", type: "error" });
          setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
        } else {
          setBanner({ show: true, message: "Popup content copied into changelog.", type: "success" });
          setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 3000);
        }
        setPendingCopyPopupContent(false);
        return ok;
      } else {
        setBanner({ show: true, message: "Copy requested. Editor is initializing…", type: "success" });
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 2000);
        // Don't set pendingCopyPopupContent to false here - let it be handled when editor becomes ready
        return true; // Return true to indicate we're waiting for editor
      }
    }, [ensurePopupData, injectPopupContentIntoEditor]);

    // Note: Injection is now handled directly in the checkbox onChange handler
    // This useEffect is kept as a fallback but shouldn't be needed
  
    /* ---------------- CHANGELOG ---------------- */
  
    async function saveChangelog() {
      if (!changelogContent) {
        setBanner({ show: true, message: "Changelog description is required", type: "error" });
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 3000);
        return;
      }

      try {
        // Combine heading and description: <h1>{heading}</h1> + description HTML
        let content = "";
        if (changelogHeading && changelogHeading.trim()) {
          // Escape HTML in heading to prevent XSS
          const headingEscaped = changelogHeading
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
          content = `<h1>${headingEscaped}</h1>`;
        }
        // Append description HTML (already sanitized from OnChangePluginWrapper)
        content += changelogContent;
        
        // Feature 1: Handle media based on context (new vs editing)
        let media = null;
        
        if (editingId) {
          // When editing: use existing media if keepExistingMedia is true
          if (keepExistingMedia && existingChangelogMedia) {
            media = {
              type: existingChangelogMedia.type,
              url: existingChangelogMedia.url
            };
          }
          // If keepExistingMedia is false, media stays null (removed)
        } else {
          // When creating: copy popup media if checkbox is checked
          if (usePopupMedia) {
            const popup = popupData?.content ? popupData : await ensurePopupData();
            if (popup && popup.content && popup.content.media) {
              // Copy media to changelog-specific location to avoid reference issues
              try {
                const copyRes = await api(`${API}/copy-media`, "POST", {
                  sourceUrl: popup.content.media.url,
                  mediaType: popup.content.media.type
                });
                if (copyRes.data && copyRes.data.url) {
                  media = {
                    type: copyRes.data.type,
                    url: copyRes.data.url
                  };
                } else {
                  // Fallback to original URL if copy fails
                  console.warn('Failed to copy media, using original URL');
                  media = {
                    type: popup.content.media.type,
                    url: popup.content.media.url
                  };
                }
              } catch (error) {
                console.error('Error copying media for changelog:', error);
                // Fallback to original URL if copy fails
                media = {
                  type: popup.content.media.type,
                  url: popup.content.media.url
                };
              }
            }
          }
        }
  
      if (editingId) {
        await api(`${API}/changelog/${editingId}`, "PUT", {
            content: content,
            media: media
        });
          setBanner({ show: true, message: "Changelog updated successfully", type: "success" });
      } else {
        // Pass preview ID if available (for new changelog)
        await api(`${API}/changelog`, "POST", {
            content: content,
            media: media,
            id: previewChangelogId // Pass the preview ID to use it
        });
          setBanner({ show: true, message: "Changelog created successfully", type: "success" });
      }
  
      setEditingId(null);
      setChangelogContent(null);
      setChangelogHeading(""); // Reset heading
      setPreviewChangelogId(null); // Reset preview ID
      setUsePopupMedia(false); // Reset checkbox after save
      setCopyPopupContent(false); // Reset checkbox after save
      setExistingChangelogMedia(null); // Reset existing media
      setKeepExistingMedia(true); // Reset keep media flag
      loadChangelogs();
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 3000);
      } catch (error) {
        setBanner({ show: true, message: error.message || "Failed to save changelog", type: "error" });
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
      }
    }
  
    async function deleteChangelog(id) {
      setConfirmDialog({
        show: true,
        message: "Are you sure you want to delete this changelog?",
        onConfirm: async () => {
          try {
      await api(`${API}/changelog/${id}`, "DELETE");
            setBanner({ show: true, message: "Changelog deleted successfully", type: "success" });
      loadChangelogs();
            setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 3000);
            setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
          } catch (error) {
            setBanner({ show: true, message: error.message || "Failed to delete changelog", type: "error" });
            setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
            setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
          }
        },
        onCancel: () => {
          setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
        }
      });
    }
  
    /* ---------------- POPUP ---------------- */
    const media = popup.mediaUrl? {
        type: popup.mediaType, // "image" | "video"
        url: popup.mediaUrl
      }
    : null;

    // Helper: upload a single chunk; returns { partNumber, etag } on success or { status: 'error', data: { partNumber, chunk } } on failure
    async function handleUploadChunk({ chunk, partNumber, uploadId, key, apiBase }) {
      const formData = new FormData();
      formData.append("chunk", chunk);
      formData.append("uploadId", uploadId);
      formData.append("key", key);
      formData.append("partNumber", String(partNumber));

      const res = await fetch(`${apiBase}/api/whats-new/upload-media/part`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { status: "error", data: { partNumber, chunk }, id: partNumber };
      }
      const partData = await res.json();
      const etag = (partData.data && partData.data.etag) || partData.etag;
      if (!etag) return { status: "error", data: { partNumber, chunk }, id: partNumber };
      return { partNumber, etag, id: partNumber, status: "success" };
    }

    // Upload chunks in parallel; retry only failed chunks until all succeed or give up
    async function sendParallelChunkRequest({ uploadId, key, data, apiBase }) {
      const results = await Promise.all(
        data.map(({ chunk, partNumber }) =>
          handleUploadChunk({ chunk, partNumber, uploadId, key, apiBase })
        )
      );
      results.sort((a, b) => (a.id ?? a.partNumber) - (b.id ?? b.partNumber));

      const failedResults = results.filter((r) => r.status === "error");
      if (failedResults.length > 0) {
        const retried = await sendParallelChunkRequest({
          uploadId,
          key,
          data: failedResults.map((r) => ({ chunk: r.data.chunk, partNumber: r.data.partNumber })),
          apiBase
        });
        retried.forEach((val) => {
          const idx = results.findIndex(
            (item) => item.status === "error" && item.data.partNumber === val.partNumber
          );
          if (idx !== -1) results[idx] = val;
        });
      }
      return results;
    }

    // Main upload: init → parallel chunk upload (with retry) → finalize
    async function performUpload(file) {
      const CHUNK_SIZE = 500 * 1024; // 500 KB – under nginx ~900KB; server reassembles and uploads to S3
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      const apiBase =
        process.env.NODE_ENV === "production"
          ? "https://admin.highperformr.ai"
          : "http://localhost:4000";

      setUploadingMedia(true);

      try {
        // 1. Init multipart on server (get server uploadId and key)
        const initRes = await fetch(`${apiBase}/api/whats-new/upload-media/init`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            folderPath: "whats-new-media", 
            fileName: file.name,
            contentType: file.type || "application/octet-stream"
          })
        });
        if (!initRes.ok) {
          const err = await initRes.json().catch(() => ({}));
          throw new Error(err.error || "Init upload failed");
        }
        const initData = await initRes.json();
        const { uploadId, key } = initData.data || initData;
        if (!uploadId || !key) throw new Error("Missing uploadId or key from init");

        // 2. Build chunk list and upload in parallel (with retry on failure)
        const chunkFiles = [];
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          chunkFiles.push({ chunk: file.slice(start, end), partNumber: i + 1 });
        }
        const response = await sendParallelChunkRequest({
          uploadId,
          key,
          data: chunkFiles,
          apiBase
        });

        const failed = response.filter((r) => r.status === "error");
        if (failed.length > 0) {
          throw new Error("Chunk upload failed after retries");
        }

        const parts = response
          .filter((r) => r.status === "success" && r.etag)
          .map((r) => ({ partNumber: r.partNumber, etag: r.etag }))
          .sort((a, b) => a.partNumber - b.partNumber);

        // 3. Finalize multipart
        const completeRes = await fetch(`${apiBase}/api/whats-new/upload-media/complete`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uploadId, key, parts })
        });

        if (!completeRes.ok) {
          const err = await completeRes.json().catch(() => ({}));
          throw new Error(err.error || "Complete upload failed");
        }

        const completeData = await completeRes.json();
        const url = completeData.url ?? (completeData.data && completeData.data.publicUrl) ?? completeData.publicUrl;
        if (!url) throw new Error("Missing URL from complete response");

        setPopup(p => ({
          ...p,
          uploadedMediaUrl: url,
          mediaUrl: url
        }));

        setBanner({ show: true, message: "Media uploaded successfully", type: "success" });
      } catch (error) {
        setBanner({ show: true, message: error.message || "Upload failed", type: "error" });
      } finally {
        setUploadingMedia(false);
      }
    }
  
    async function savePopup() {
      try {
        // Use uploaded URL if available, otherwise use manual URL
        const finalMediaUrl = popup.uploadedMediaUrl || popup.mediaUrl;
        const media =
          finalMediaUrl
            ? {
                type: popup.mediaType,
                url: finalMediaUrl
              }
            : null;
      
        // 1. Validate media (if exists)
        if (media) {
            const inferred = inferMediaTypeFromUrl(media.url);
            if (inferred && inferred !== media.type) {
              return new Promise((resolve) => {
                setConfirmDialog({
                  show: true,
                  message: `⚠️ Media Type Mismatch:\n\nSelected: ${media.type}\nDetected: ${inferred}\n\nUse detected type instead?`,
                  onConfirm: async () => {
                    media.type = inferred;
                    setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
                    await continueSavePopup(media);
                    resolve();
                  },
                  onCancel: () => {
                    setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
                    resolve();
                  }
                });
              });
            }
        }
      
        // 2. Save directly if no validation needed
        await continueSavePopup(media);
      } catch (error) {
        setBanner({ show: true, message: error.message || "Failed to save popup", type: "error" });
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
      }
    }

    async function continueSavePopup(media) {
      try {
        // Construct knowMore URL from changelog ID if provided
        let knowMore = null;
        if (popup.changelogId && popup.changelogId.trim()) {
          knowMore = {
            text: "Know More",
            url: `https://www.highperformr.ai/changelogs#${popup.changelogId.trim()}`
          };
        }
        
        const res = await api(`${API}/popup`, "PUT", {
        heading: popup.heading,
        description: popup.description,
          media,
          knowMore: knowMore,
          tryNow: popup.tryNowUrl
            ? { text: "Try Now", url: popup.tryNowUrl, openInSameTab: popup.openInSameTab }
          : null,
          publishTarget
      });
      
        // Production saves update DynamoDB source of truth used by changelog copy helpers
        if (res && res.data && publishTarget === "production") {
          console.log('Updating popupData after save:', res.data);
          setPopupData(res.data);
          // Clear the fetch promise cache so next fetch gets fresh data
          popupFetchPromiseRef.current = null;
        }

        await loadStagingStatus();

        const targetLabel = publishTarget === "staging" ? "staging" : "production";
        setBanner({ show: true, message: `Popup saved to ${targetLabel}`, type: "success" });
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 3000);
      } catch (error) {
        setBanner({ show: true, message: error.message || "Failed to save popup", type: "error" });
        setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
      }
    }

    async function promoteStagingPopup() {
      setConfirmDialog({
        show: true,
        message: "Push the staging popup to production? This will overwrite the live popup and remove the staging popup.",
        onConfirm: async () => {
          setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
          setStagingActionLoading(true);
          try {
            const res = await api(`${API}/promote-staging`, "POST");
            if (res?.data?.popup) {
              setPopupData(res.data.popup);
              popupFetchPromiseRef.current = null;
              await loadPopup();
            }
            setStagingBannerAction("promote");
            await loadStagingStatus();
            setBanner({ show: true, message: "Staging popup pushed to production.", type: "success" });
            setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 4000);
          } catch (error) {
            setBanner({ show: true, message: error.message || "Failed to push staging to production", type: "error" });
            setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
          } finally {
            setStagingActionLoading(false);
          }
        },
        onCancel: () => {
          setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
        }
      });
    }

    async function deleteStagingPopupAction() {
      setConfirmDialog({
        show: true,
        message: "Delete the staging popup? This removes it from staging and does not change production.",
        onConfirm: async () => {
          setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
          setStagingActionLoading(true);
          try {
            await api(`${API}/staging-popup`, "DELETE");
            setStagingBannerAction("promote");
            await loadStagingStatus();
            setBanner({ show: true, message: "Staging popup deleted.", type: "success" });
            setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 4000);
          } catch (error) {
            setBanner({ show: true, message: error.message || "Failed to delete staging popup", type: "error" });
            setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
          } finally {
            setStagingActionLoading(false);
          }
        },
        onCancel: () => {
          setConfirmDialog({ show: false, message: "", onConfirm: null, onCancel: null });
        }
      });
    }

    function handleStagingBannerConfirm() {
      if (stagingBannerAction === "delete") {
        deleteStagingPopupAction();
      } else {
        promoteStagingPopup();
      }
    }
      
  
    const inputStyle = {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #ddd",
      borderRadius: "6px",
      fontSize: "14px",
      fontFamily: "inherit",
      marginTop: "8px",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease",
    };

    const labelStyle = {
      display: "block",
      fontWeight: "600",
      fontSize: "14px",
      color: "#495057",
      marginTop: "20px",
      marginBottom: "8px"
    };

    const buttonStyle = {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "600",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginRight: "10px",
      marginTop: "10px"
    };

    const primaryButtonStyle = {
      ...buttonStyle,
      backgroundColor: "#007bff",
      color: "white"
    };

    const secondaryButtonStyle = {
      ...buttonStyle,
      backgroundColor: "#6c757d",
      color: "white"
    };

    const dangerButtonStyle = {
      ...buttonStyle,
      backgroundColor: "#dc3545",
      color: "white"
    };

    const cardStyle = {
      backgroundColor: "white",
      borderRadius: "8px",
      padding: "24px",
      marginBottom: "24px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      border: "1px solid #e9ecef"
    };

    return (
        <div style={{ padding: "30px", maxWidth: "1000px", margin: "0 auto", backgroundColor: "#f8f9fa", minHeight: "100vh", position: "relative" }}>
          {/* Banner Notification */}
          {banner.show && (
            <div style={{
              position: "fixed",
              top: "20px",
              right: "20px",
              backgroundColor: banner.type === "success" ? "#28a745" : "#dc3545",
              color: "white",
              padding: "15px 20px",
              borderRadius: "6px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              minWidth: "300px",
              maxWidth: "500px",
              animation: "slideIn 0.3s ease-out"
            }}>
              <span style={{ fontSize: "20px" }}>
                {banner.type === "success" ? "✓" : "✕"}
              </span>
              <span style={{ flex: 1 }}>{banner.message}</span>
              <button
                onClick={() => setBanner({ show: false, message: "", type: "success" })}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "white",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "0",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Confirmation Dialog */}
          {confirmDialog.show && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10001
            }}>
              <div style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "24px",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
              }}>
                <div style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#2c3e50",
                  marginBottom: "16px"
                }}>
                  Confirmation
                </div>
                <div style={{
                  color: "#495057",
                  marginBottom: "24px",
                  whiteSpace: "pre-line",
                  lineHeight: "1.6"
                }}>
                  {confirmDialog.message}
                </div>
                <div style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px"
                }}>
                  <button
                    onClick={confirmDialog.onCancel}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: "white",
                      color: "#495057"
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDialog.onConfirm}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: "#28a745",
                      color: "white"
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#218838"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "#28a745"}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <style>{`
            @keyframes slideIn {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
          
          <h1 style={{ 
            fontSize: "28px", 
            fontWeight: "bold", 
            color: "#2c3e50",
            marginBottom: "20px"
          }}>
            What's New
          </h1>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
            <button
              onClick={() => setActiveTab('configuration')}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: activeTab === 'configuration' ? '600' : '400',
                border: 'none',
                borderRadius: '6px 6px 0 0',
                backgroundColor: activeTab === 'configuration' ? '#007bff' : '#e9ecef',
                color: activeTab === 'configuration' ? '#fff' : '#2c3e50',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                margin: 0,
                borderBottom: activeTab === 'configuration' ? '3px solid #007bff' : '3px solid transparent'
              }}
            >
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('history')}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: activeTab === 'history' ? '600' : '400',
                border: 'none',
                borderRadius: '6px 6px 0 0',
                backgroundColor: activeTab === 'history' ? '#007bff' : '#e9ecef',
                color: activeTab === 'history' ? '#fff' : '#2c3e50',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                margin: 0,
                borderBottom: activeTab === 'history' ? '3px solid #007bff' : '3px solid transparent'
              }}
            >
              History
            </button>
          </div>

          {activeTab === 'configuration' && (
            <>
              {/* ---------------- POPUP ---------------- */}
              <section style={cardStyle}>
            <h2 style={{ 
              fontSize: "20px", 
              fontWeight: "600", 
              color: "#2c3e50",
              marginBottom: "20px",
              borderBottom: "2px solid #007bff",
              paddingBottom: "10px"
            }}>
              Popup Configuration
            </h2>

            {stagingExists && (
              <div style={{
                marginTop: "0",
                marginBottom: "20px",
                padding: "16px 18px",
                backgroundColor: "#fff3cd",
                border: "2px solid #ffc107",
                borderLeft: "6px solid #e0a800",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(224, 168, 0, 0.25)"
              }}>
                <div style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#856404",
                  marginBottom: "12px",
                  letterSpacing: "0.01em"
                }}>
                  A popup is present in staging
                </div>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "14px"
                }}>
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#495057"
                  }}>
                    <input
                      type="radio"
                      name="stagingBannerAction"
                      value="promote"
                      checked={stagingBannerAction === "promote"}
                      onChange={() => setStagingBannerAction("promote")}
                      style={{ marginRight: "8px", cursor: "pointer" }}
                    />
                    Do you want to push it to production?
                  </label>
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#495057"
                  }}>
                    <input
                      type="radio"
                      name="stagingBannerAction"
                      value="delete"
                      checked={stagingBannerAction === "delete"}
                      onChange={() => setStagingBannerAction("delete")}
                      style={{ marginRight: "8px", cursor: "pointer" }}
                    />
                    Do you want to delete it?
                  </label>
                </div>
                <button
                  onClick={handleStagingBannerConfirm}
                  disabled={stagingActionLoading}
                  style={{
                    ...primaryButtonStyle,
                    backgroundColor: stagingActionLoading
                      ? "#6c757d"
                      : (stagingBannerAction === "delete" ? "#dc3545" : "#28a745"),
                    cursor: stagingActionLoading ? "not-allowed" : "pointer",
                    opacity: stagingActionLoading ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!stagingActionLoading) {
                      e.target.style.backgroundColor = stagingBannerAction === "delete" ? "#c82333" : "#218838";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!stagingActionLoading) {
                      e.target.style.backgroundColor = stagingBannerAction === "delete" ? "#dc3545" : "#28a745";
                    }
                  }}
                >
                  {stagingActionLoading
                    ? "Working…"
                    : (stagingBannerAction === "delete" ? "Delete staging popup" : "Push staging → production")}
                </button>
              </div>
            )}
    
            <label style={labelStyle}>Heading</label>
            <input
              type="text"
              value={typeof popup.heading === 'string' ? popup.heading : ''}
              onChange={(e) => {
                setPopup(p => ({ ...p, heading: e.target.value || '' }));
              }}
              placeholder="Write here…"
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
    
            <label style={labelStyle}>Description</label>
            <textarea
              value={typeof popup.description === 'string' ? popup.description : ''}
              onChange={(e) => setPopup(p => ({ ...p, description: e.target.value }))}
              placeholder="Enter popup description..."
              style={{
                ...inputStyle,
                minHeight: '120px',
                resize: 'vertical',
                fontFamily: 'inherit',
                fontSize: '14px',
                lineHeight: '1.5'
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
            <label style={labelStyle}>Media Type</label>
            <select
            value={popup.mediaType}
            onChange={(e) =>
                setPopup(p => ({ ...p, mediaType: e.target.value }))
            }
            style={inputStyle}
            >
                <option value="image">Image / GIF</option>
                <option value="video">Video (MP4 / WebM)</option>
                </select>
    
            <label style={labelStyle}>Media Source</label>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="mediaSource"
                  value="url"
                  checked={popup.mediaSource === 'url'}
                  onChange={(e) => setPopup(p => ({ ...p, mediaSource: e.target.value, mediaFile: null, uploadedMediaUrl: '' }))}
                  style={{ marginRight: '6px' }}
                />
                URL
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="mediaSource"
                  value="upload"
                  checked={popup.mediaSource === 'upload'}
                  onChange={(e) => setPopup(p => ({ ...p, mediaSource: e.target.value, mediaUrl: '' }))}
                  style={{ marginRight: '6px' }}
                />
                Upload File
              </label>
            </div>

            {popup.mediaSource === 'url' ? (
              <>
                <label style={labelStyle}>Media URL (optional)</label>
                <input
                  type="text"
                  value={popup.mediaUrl}
                  onChange={e => setPopup(p => ({ ...p, mediaUrl: e.target.value }))}
                  placeholder="https://example.com/image.png"
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = "#007bff"}
                  onBlur={(e) => e.target.style.borderColor = "#ddd"}
                />
              </>
            ) : (
              <>
                <label style={labelStyle}>Upload Media File (optional)</label>
                <input
                  type="file"
                  accept={popup.mediaType === 'image' ? 'image/*' : 'video/*'}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    // Reset file input
                    e.target.value = '';

                    // Validation functions
                    const getImageDimensions = (file) => {
                      return new Promise((resolve) => {
                        const img = new Image();
                        const url = URL.createObjectURL(file);
                        img.onload = () => {
                          URL.revokeObjectURL(url);
                          resolve({ width: img.width, height: img.height });
                        };
                        img.onerror = () => {
                          URL.revokeObjectURL(url);
                          resolve(null);
                        };
                        img.src = url;
                      });
                    };

                    const getVideoDuration = (file) => {
                      return new Promise((resolve) => {
                        const video = document.createElement('video');
                        const url = URL.createObjectURL(file);
                        video.onloadedmetadata = () => {
                          URL.revokeObjectURL(url);
                          resolve(video.duration);
                        };
                        video.onerror = () => {
                          URL.revokeObjectURL(url);
                          resolve(null);
                        };
                        video.src = url;
                      });
                    };

                    const calculateAspectRatio = (width, height) => {
                      if (!width || !height) return null;
                      const ratio = width / height;
                      // Check if close to 3:2 (1.5) or 16:10 (1.6)
                      const is32 = Math.abs(ratio - 1.5) < 0.1;
                      const is1610 = Math.abs(ratio - 1.6) < 0.1;
                      return { ratio, is32, is1610, preferred: is32 || is1610 };
                    };

                    const formatFileSize = (bytes) => {
                      if (bytes < 1024) return bytes + ' B';
                      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
                      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
                    };

                    // Hard reject: File type validation
                    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                    const allowedVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
                    const isImage = popup.mediaType === 'image';
                    const isVideo = popup.mediaType === 'video';
                    
                    if (isImage && !allowedImageTypes.includes(file.type)) {
                      setBanner({ 
                        show: true, 
                        message: `File type not allowed. Allowed types: ${allowedImageTypes.join(', ')}`, 
                        type: "error" 
                      });
                      setTimeout(() => setBanner({ show: false, message: "", type: "error" }), 5000);
                      return;
                    }
                    
                    if (isVideo && !allowedVideoTypes.includes(file.type)) {
                      setBanner({ 
                        show: true, 
                        message: `File type not allowed. Allowed types: ${allowedVideoTypes.join(', ')}`, 
                        type: "error" 
                      });
                      setTimeout(() => setBanner({ show: false, message: "", type: "error" }), 5000);
                      return;
                    }

                    // Proceed directly with upload (no warnings)
                    await performUpload(file);
                  }}
                  style={inputStyle}
                  disabled={uploadingMedia}
                />
                {uploadingMedia && (
                  <div style={{ marginTop: '8px', color: '#007bff', fontSize: '13px' }}>
                    Uploading...
                  </div>
                )}
                {popup.uploadedMediaUrl && (
                  <div style={{ marginTop: '12px' }}>
                    {popup.mediaType === 'image' ? (
                      <img 
                        src={popup.uploadedMediaUrl} 
                        alt="Preview" 
                        style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '6px', border: '1px solid #ddd' }}
                      />
                    ) : (
                      <video 
                        src={popup.uploadedMediaUrl} 
                        controls 
                        style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '6px', border: '1px solid #ddd' }}
                      />
                    )}
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                      Uploaded: <a href={popup.uploadedMediaUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>{popup.uploadedMediaUrl}</a>
                    </div>
                  </div>
                )}
              </>
            )}
    
            <label style={labelStyle}>Changelog ID (optional)</label>
            <input
              type="text"
              placeholder="e.g., clg_abc123def456"
              value={popup.changelogId}
              onChange={e => setPopup(p => ({ ...p, changelogId: e.target.value }))}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
            <div style={{ marginTop: "4px", fontSize: "12px", color: "#6c757d" }}>
              If provided, "Know More" will link to: https://www.highperformr.ai//changelogs#{popup.changelogId || "changelog_id"}
            </div>
    
            <label style={labelStyle}>Try Now URL (optional)</label>
            <input
              type="text"
              placeholder="https://example.com/try-now"
              value={popup.tryNowUrl}
              onChange={e => setPopup(p => ({ ...p, tryNowUrl: e.target.value }))}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
            <label style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              color: "#495057",
              marginTop: "8px"
            }}>
              <input
                type="checkbox"
                checked={popup.openInSameTab}
                onChange={e => setPopup(p => ({ ...p, openInSameTab: e.target.checked }))}
                style={{
                  marginRight: "8px",
                  width: "16px",
                  height: "16px",
                  cursor: "pointer"
                }}
              />
              <span>Open &quot;Try Now&quot; in the same tab</span>
            </label>

            <label style={labelStyle}>Publish to</label>
            <div style={{ display: "flex", gap: "20px", marginTop: "8px" }}>
              <label style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                color: "#495057"
              }}>
                <input
                  type="radio"
                  name="publishTarget"
                  value="production"
                  checked={publishTarget === "production"}
                  onChange={() => setPublishTarget("production")}
                  style={{ marginRight: "8px", cursor: "pointer" }}
                />
                Production
              </label>
              <label style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                color: "#495057"
              }}>
                <input
                  type="radio"
                  name="publishTarget"
                  value="staging"
                  checked={publishTarget === "staging"}
                  onChange={() => setPublishTarget("staging")}
                  style={{ marginRight: "8px", cursor: "pointer" }}
                />
                Staging
              </label>
            </div>
            <div style={{ marginTop: "4px", fontSize: "12px", color: "#6c757d" }}>
              Staging is for preview. Production goes live.
            </div>
    
            <div style={{ marginTop: "24px", display: "flex", gap: "10px" }}>
              <button 
                onClick={savePopup}
                style={primaryButtonStyle}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#0056b3"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "#007bff"}
              >
                💾 Save Popup
              </button>
            </div>
          </section>
    
          {/* ---------------- CHANGELOG ---------------- */}
          <section style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ 
                fontSize: "20px", 
                fontWeight: "600", 
                color: "#2c3e50",
                margin: 0,
                borderBottom: "2px solid #28a745",
                paddingBottom: "10px",
                flex: 1
              }}>
                Changelog Management
              </h2>
                  <button 
                    onClick={() => {
                      const previewId = generatePreviewChangelogId();
                      setPreviewChangelogId(previewId);
                      setEditingId(null);
                      setChangelogContent("");
                      setChangelogHeading("");
                    }}
                    style={{
                      ...primaryButtonStyle,
                      backgroundColor: "#28a745",
                      margin: 0
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#218838"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "#28a745"}
                  >
                    ➕ New Changelog
                  </button>
            </div>
    
                {(editingId !== null || changelogContent !== null) && (
                  <div style={{
                    backgroundColor: "#f8f9fa",
                    padding: "20px",
                    borderRadius: "6px",
                    border: "2px solid #007bff",
                    marginBottom: "20px",
                    overflow: "hidden",
                    boxSizing: "border-box"
                  }}>
                    {/* Feature checkboxes - Different UI for editing vs creating */}
                    {editingId ? (
                      /* Editing mode: Show existing media with keep/remove option */
                      existingChangelogMedia ? (
                        <div style={{ 
                          marginBottom: "16px", 
                          padding: "12px", 
                          backgroundColor: "#fff", 
                          borderRadius: "6px",
                          border: "1px solid #e9ecef"
                        }}>
                          <div style={{ 
                            fontSize: "14px", 
                            fontWeight: "600", 
                            color: "#495057", 
                            marginBottom: "12px" 
                          }}>
                            Media currently used in this changelog:
                          </div>
                          <div style={{ 
                            marginBottom: "12px",
                            padding: "12px", 
                            backgroundColor: "#f8f9fa", 
                            borderRadius: "6px",
                            border: "1px solid #e9ecef"
                          }}>
                            {existingChangelogMedia.type === 'image' ? (
                              <img 
                                src={existingChangelogMedia.url} 
                                alt="Changelog media" 
                                style={{ 
                                  maxWidth: '300px', 
                                  maxHeight: '200px', 
                                  borderRadius: '6px', 
                                  border: '1px solid #ddd' 
                                }}
                              />
                            ) : (
                              <video 
                                src={existingChangelogMedia.url} 
                                controls 
                                style={{ 
                                  maxWidth: '300px', 
                                  maxHeight: '200px', 
                                  borderRadius: '6px', 
                                  border: '1px solid #ddd' 
                                }}
                              />
                            )}
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                              <strong>Type:</strong> {existingChangelogMedia.type}
                              <br />
                              <strong>URL:</strong> <a href={existingChangelogMedia.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', wordBreak: 'break-all' }}>{existingChangelogMedia.url}</a>
                            </div>
                          </div>
                          <label style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#495057"
                          }}>
                            <input
                              type="checkbox"
                              checked={keepExistingMedia}
                              onChange={(e) => setKeepExistingMedia(e.target.checked)}
                              style={{ 
                                marginRight: "8px",
                                width: "16px",
                                height: "16px",
                                cursor: "pointer"
                              }}
                            />
                            <span>Keep using this media</span>
                          </label>
                          {!keepExistingMedia && (
                            <div style={{ 
                              marginTop: "8px",
                              padding: "8px",
                              backgroundColor: "#fff3cd",
                              borderRadius: "4px",
                              fontSize: "12px",
                              color: "#856404"
                            }}>
                              ⚠️ Media will be removed when you save.
                            </div>
                          )}
                        </div>
                      ) : null
                    ) : (
                      /* Creating mode: Show popup copy options */
                      <div style={{ 
                        marginBottom: "16px", 
                        padding: "12px", 
                        backgroundColor: "#fff", 
                        borderRadius: "6px",
                        border: "1px solid #e9ecef"
                      }}>
                        <div style={{ 
                          display: "flex", 
                          flexDirection: "column", 
                          gap: "12px" 
                        }}>
                          <label style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#495057"
                          }}>
                            <input
                              type="checkbox"
                              checked={copyPopupContent || pendingCopyPopupContent}
                              onChange={async (e) => {
                                const checked = e.target.checked;
                                setCopyPopupContent(checked);

                                // One-time action: when turned ON, fetch popup (if needed) and inject
                                if (checked) {
                                  await handleCopyPopupHeadingDescription();
                                  // Keep checkbox checked - user can uncheck manually if they want
                                  // Content will remain in editor even if unchecked
                                }
                                // If unchecked, do nothing - content stays in editor
                              }}
                              style={{ 
                                marginRight: "8px",
                                width: "16px",
                                height: "16px",
                                cursor: "pointer"
                              }}
                            />
                            <span>Copy popup heading & description</span>
                          </label>
                          <label style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#495057"
                          }}>
                            <input
                              type="checkbox"
                              checked={usePopupMedia}
                              onChange={async (e) => {
                                const checked = e.target.checked;
                                setUsePopupMedia(checked);
                                
                                // If checking and popup data not loaded, fetch it
                                if (checked && (!popupData || !popupData.content)) {
                                  await ensurePopupData();
                                }
                              }}
                              style={{ 
                                marginRight: "8px",
                                width: "16px",
                                height: "16px",
                                cursor: "pointer"
                              }}
                            />
                            <span>Use popup media</span>
                          </label>
                        </div>
                        
                        {/* Media preview when "Use popup media" is checked */}
                        {usePopupMedia && (() => {
                          const media = popupData?.content?.media;
                          if (media && media.url) {
                            return (
                              <div style={{ 
                                marginTop: "12px", 
                                padding: "12px", 
                                backgroundColor: "#f8f9fa", 
                                borderRadius: "6px",
                                border: "1px solid #e9ecef"
                              }}>
                                <div style={{ 
                                  fontSize: "12px", 
                                  fontWeight: "600", 
                                  color: "#6c757d", 
                                  marginBottom: "8px" 
                                }}>
                                  Media that will be used:
                                </div>
                                {media.type === 'image' ? (
                                  <img 
                                    src={media.url} 
                                    alt="Popup media preview" 
                                    style={{ 
                                      maxWidth: '300px', 
                                      maxHeight: '200px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #ddd' 
                                    }}
                                  />
                                ) : (
                                  <video 
                                    src={media.url} 
                                    controls 
                                    style={{ 
                                      maxWidth: '300px', 
                                      maxHeight: '200px', 
                                      borderRadius: '6px', 
                                      border: '1px solid #ddd' 
                                    }}
                                  />
                                )}
                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                                  <strong>Type:</strong> {media.type}
                                  <br />
                                  <strong>URL:</strong> <a href={media.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', wordBreak: 'break-all' }}>{media.url}</a>
                                </div>
                              </div>
                            );
                          } else if (usePopupMedia && (!popupData || !popupData.content || !popupData.content.media)) {
                            return (
                              <div style={{ 
                                marginTop: "12px", 
                                padding: "12px", 
                                backgroundColor: "#fff3cd", 
                                borderRadius: "6px",
                                border: "1px solid #ffc107",
                                fontSize: "12px",
                                color: "#856404"
                              }}>
                                ⚠️ Popup media not found. Please ensure the popup has media configured.
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                    
                    {/* Changelog ID Display */}
                    {editingId ? (
                      <div style={{ 
                        marginBottom: "16px", 
                        padding: "12px", 
                        backgroundColor: "#e7f3ff", 
                        borderRadius: "6px",
                        border: "1px solid #b3d9ff",
                        fontSize: "14px"
                      }}>
                        <strong>Changelog ID:</strong> <code style={{ 
                          backgroundColor: "#fff", 
                          padding: "4px 8px", 
                          borderRadius: "4px",
                          fontFamily: "monospace",
                          fontSize: "13px"
                        }}>{editingId}</code>
                      </div>
                    ) : previewChangelogId ? (
                      <div style={{ 
                        marginBottom: "16px", 
                        padding: "12px", 
                        backgroundColor: "#fff3cd", 
                        borderRadius: "6px",
                        border: "1px solid #ffc107",
                        fontSize: "14px"
                      }}>
                        <strong>Changelog ID (preview):</strong> <code style={{ 
                          backgroundColor: "#fff", 
                          padding: "4px 8px", 
                          borderRadius: "4px",
                          fontFamily: "monospace",
                          fontSize: "13px"
                        }}>{previewChangelogId}</code>
                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#856404" }}>
                          This ID will be used when you save the changelog.
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Heading Input */}
                    <label style={labelStyle}>Heading (will be wrapped in &lt;h1&gt; tag)</label>
                    <input
                      type="text"
                      value={changelogHeading}
                      onChange={(e) => setChangelogHeading(e.target.value)}
                      placeholder="Enter changelog heading..."
                      style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = "#007bff"}
                      onBlur={(e) => e.target.style.borderColor = "#ddd"}
                    />
                    
                    {/* Description Editor */}
                    <label style={labelStyle}>Description</label>
                    <LexicalEditor
                      value={changelogContent}
                      onChange={(safeHtml) => setChangelogContent(safeHtml)}
                      onEditorReady={(editor) => {
                        changelogEditorRef.current = editor;
                        // If user clicked copy before editor was ready, complete it now
                        if (pendingCopyPopupContent && popupData?.content) {
                          const ok = injectPopupContentIntoEditor(popupData);
                          if (ok) {
                            setBanner({ show: true, message: "Popup content copied into changelog.", type: "success" });
                            setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 3000);
                          } else {
                            setBanner({ show: true, message: "Popup heading/description is empty.", type: "error" });
                            setTimeout(() => setBanner({ show: false, message: "", type: "success" }), 5000);
                          }
                          setPendingCopyPopupContent(false);
                          // Keep checkbox checked - user can uncheck manually if they want
                          // Content will remain in editor even if unchecked
                        }
                      }}
                    />
                    
                    <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                      <button 
                        onClick={saveChangelog}
                        style={primaryButtonStyle}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#0056b3"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#007bff"}
                      >
                        💾 Save
                      </button>
                      <button 
                        onClick={() => {
                          setChangelogContent(null);
                          setChangelogHeading("");
                          setEditingId(null);
                          setPreviewChangelogId(null);
                          setUsePopupMedia(false);
                          setCopyPopupContent(false);
                        }}
                        style={secondaryButtonStyle}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#5a6268"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#6c757d"}
                      >
                        ❌ Cancel
                      </button>
                    </div>
                  </div>
                )}
        
                {editingId === null && changelogContent === null && (
                  <div style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#6c757d",
                    fontStyle: "italic"
                  }}>
                    Click "+ New Changelog" to create a new changelog.
                  </div>
                )}
              </section>
            </>
          )}

          {activeTab === 'history' && (
            <section style={cardStyle}>
              <h2 style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#2c3e50",
                marginBottom: "20px",
                borderBottom: "2px solid #17a2b8",
                paddingBottom: "10px"
              }}>
                Changelog History
              </h2>

              {changelogs.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px", color: "#6c757d", fontStyle: "italic" }}>
                  No changelogs found.
                </div>
              )}

              {changelogs.map(c => (
                <div 
                  key={c.id} 
                  style={{ 
                    border: "1px solid #e9ecef", 
                    marginTop: "16px",
                    borderRadius: "6px",
                    padding: "20px",
                    backgroundColor: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                  }}
                >
                  <LexicalViewer content={c.content} />
        
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "15px",
                    paddingTop: "15px",
                    borderTop: "1px solid #e9ecef"
                  }}>
                    <small style={{ color: "#6c757d", fontSize: "12px" }}>
                      📅 Updated: {new Date(c.updated_at).toLocaleString()}
                    </small>
        
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button 
                        onClick={() => {
                          setEditingId(c.id);
                          // Extract heading from content (if it starts with <h1>)
                          const content = typeof c.content === 'string' ? c.content : '';
                          let heading = '';
                          let description = content;
                          
                          // Check if content starts with <h1> tag
                          const h1Match = content.match(/^<h1[^>]*>(.*?)<\/h1>/i);
                          if (h1Match) {
                            // Extract heading text (unescape HTML entities)
                            heading = h1Match[1]
                              .replace(/&amp;/g, '&')
                              .replace(/&lt;/g, '<')
                              .replace(/&gt;/g, '>')
                              .replace(/&quot;/g, '"')
                              .replace(/&#039;/g, "'");
                            // Remove the <h1> tag from description
                            description = content.replace(/^<h1[^>]*>.*?<\/h1>/i, '').trim();
                          }
                          
                          setChangelogHeading(heading);
                          setChangelogContent(description);
                          // Load existing media if present
                          setExistingChangelogMedia(c.media || null);
                          setKeepExistingMedia(c.media ? true : false);
                          setActiveTab('configuration'); // Switch to configuration tab when editing
                        }}
                        style={{
                          ...buttonStyle,
                          backgroundColor: "#17a2b8",
                          color: "white",
                          padding: "6px 12px",
                          fontSize: "12px",
                          margin: 0
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#138496"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#17a2b8"}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        onClick={() => deleteChangelog(c.id)}
                        style={{
                          ...buttonStyle,
                          backgroundColor: "#dc3545",
                          color: "white",
                          padding: "6px 12px",
                          fontSize: "12px",
                          margin: 0
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#c82333"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#dc3545"}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </section>
          )}
        </div>
      );
    }